import { User } from "../../types/Types";
import { resolvedObjectId } from "../../utils/connectionUserUtils";

/** Event user from API - may include userId for fetching full profile */
export interface EventUserRecord {
  userId?: string;
  firstName: string;
  lastName: string;
  email: string;
  [key: string]: unknown;
}

export const BACK_LINK = "/event-booking";
export const PAGE_SIZE = 10;

export function pickField(
  r: Record<string, unknown>,
  camel: string,
  pascal: string
): unknown {
  return r[camel] ?? r[pascal];
}

/**
 * API rows may nest the member under `user` / `User`. Nested fields should win
 * for identity (objectId, names) when the root duplicates the viewer on every row.
 */
export function flattenEventUserRow(raw: EventUserRecord): Record<string, unknown> {
  const r = raw as Record<string, unknown>;
  const nested =
    pickField(r, "user", "User") ??
    pickField(r, "applicationUser", "ApplicationUser") ??
    pickField(r, "member", "Member");
  if (nested && typeof nested === "object" && !Array.isArray(nested)) {
    return { ...r, ...(nested as Record<string, unknown>) };
  }
  return r;
}

export function getEventAttendeeObjectId(raw: EventUserRecord): string {
  const r = flattenEventUserRow(raw);
  const id =
    pickField(r, "objectId", "ObjectId") ??
    pickField(r, "userId", "UserId") ??
    pickField(r, "azureAdObjectId", "AzureAdObjectId");
  return String(id ?? "").trim();
}

export function stableNumericId(objectId: string, index: number): number {
  if (!objectId) return -(index + 1);
  let h = 0;
  for (let i = 0; i < objectId.length; i++) {
    h = (Math.imul(31, h) + objectId.charCodeAt(i)) | 0;
  }
  const abs = Math.abs(h);
  return abs > 0 ? abs : index + 1;
}

export function normalizedEmail(user: Pick<User, "email"> | null | undefined): string {
  return String(user?.email ?? "").trim().toLowerCase();
}

export function normalizedFullName(
  user: Pick<User, "firstName" | "lastName"> | null | undefined
): string {
  return `${user?.firstName ?? ""} ${user?.lastName ?? ""}`.trim().toLowerCase();
}

/**
 * Event attendee rows can carry inconsistent ids/objectIds, so prefer the same
 * stable identifiers we rely on in the Connections area: email first, then
 * objectId, then numeric id only when there is no contradictory name data.
 */
export function sameConnectionUser(attendee: User, candidate: User): boolean {
  const attendeeEmail = normalizedEmail(attendee);
  const candidateEmail = normalizedEmail(candidate);
  if (attendeeEmail && candidateEmail) {
    return attendeeEmail === candidateEmail;
  }

  if (
    typeof attendee.id === "number" &&
    attendee.id > 0 &&
    typeof candidate.id === "number" &&
    candidate.id > 0 &&
    attendee.id === candidate.id
  ) {
    const attendeeName = normalizedFullName(attendee);
    const candidateName = normalizedFullName(candidate);
    return !attendeeName || !candidateName || attendeeName === candidateName;
  }

  const attendeeOid = resolvedObjectId(attendee).toLowerCase();
  const candidateOid = resolvedObjectId(candidate).toLowerCase();
  if (attendeeOid && candidateOid) {
    if (attendeeOid !== candidateOid) return false;
    const attendeeName = normalizedFullName(attendee);
    const candidateName = normalizedFullName(candidate);
    return !attendeeName || !candidateName || attendeeName === candidateName;
  }

  return false;
}

export function mergeAttendeeDisplayUser(base: User, preferred?: User | null): User {
  if (!preferred) return base;

  return {
    ...preferred,
    ...base,
    id: preferred.id || base.id,
    objectId: resolvedObjectId(preferred) || base.objectId,
    membershipId: preferred.membershipId || base.membershipId,
    membershipName: preferred.membershipName || base.membershipName,
    explorerAttendeeType:
      preferred.explorerAttendeeType || base.explorerAttendeeType,
    eventObjectives: base.eventObjectives || preferred.eventObjectives,
    city: preferred.city || base.city,
    country: preferred.country || base.country,
    phoneNumber: preferred.phoneNumber || base.phoneNumber,
    professionalPhoto:
      preferred.professionalPhoto ||
      preferred.ProfessionalPhoto ||
      preferred.personalProfessionalPhoto ||
      preferred.PersonalProfessionalPhoto ||
      base.professionalPhoto ||
      base.ProfessionalPhoto ||
      base.personalProfessionalPhoto ||
      base.PersonalProfessionalPhoto ||
      "",
    ProfessionalPhoto:
      preferred.ProfessionalPhoto ||
      preferred.professionalPhoto ||
      preferred.PersonalProfessionalPhoto ||
      preferred.personalProfessionalPhoto ||
      base.ProfessionalPhoto ||
      base.professionalPhoto ||
      base.PersonalProfessionalPhoto ||
      base.personalProfessionalPhoto ||
      "",
    personalProfessionalPhoto:
      preferred.personalProfessionalPhoto ||
      preferred.PersonalProfessionalPhoto ||
      preferred.professionalPhoto ||
      preferred.ProfessionalPhoto ||
      base.personalProfessionalPhoto ||
      base.PersonalProfessionalPhoto ||
      base.professionalPhoto ||
      base.ProfessionalPhoto ||
      "",
    PersonalProfessionalPhoto:
      preferred.PersonalProfessionalPhoto ||
      preferred.personalProfessionalPhoto ||
      preferred.ProfessionalPhoto ||
      preferred.professionalPhoto ||
      base.PersonalProfessionalPhoto ||
      base.personalProfessionalPhoto ||
      base.ProfessionalPhoto ||
      base.professionalPhoto ||
      "",
  };
}

/**
 * Build a User from /Event/event/:id/users row (same source as admin Event Details).
 * Used when ByObjectId returns the wrong user (e.g. always the signed-in user for non-admins).
 */
export function userFromEventAttendeeRecord(raw: EventUserRecord, index: number): User {
  const r = flattenEventUserRow(raw);
  const str = (camel: string, pascal: string) =>
    String(pickField(r, camel, pascal) ?? "").trim();
  const objectId = getEventAttendeeObjectId(raw);
  const idRaw = pickField(r, "id", "Id");
  const parsed = typeof idRaw === "number" ? idRaw : Number(idRaw);
  const id =
    Number.isFinite(parsed) && parsed > 0 ? parsed : stableNumericId(objectId, index);

  const membershipName =
    str("membershipName", "MembershipName") ||
    str("membership", "Membership");

  const professionalPhoto = str("professionalPhoto", "ProfessionalPhoto");
  const personalProfessionalPhoto = str(
    "personalProfessionalPhoto",
    "PersonalProfessionalPhoto"
  );

  return {
    id,
    objectId,
    firstName: str("firstName", "FirstName"),
    lastName: str("lastName", "LastName"),
    email: str("email", "Email"),
    isGDPRConsent: 0,
    isEmailUpdates: 0,
    isAnnualMembership: 0,
    membershipId: Number(pickField(r, "membershipId", "MembershipId")) || 0,
    membershipName,
    explorerAttendeeType:
      str("explorerAttendeeType", "ExplorerAttendeeType") || undefined,
    isDirectory: 0,
    isTermConditions: 0,
    hasPaid: 0,
    dateOfBirth: str("dateOfBirth", "DateOfBirth"),
    idDocument: "",
    phoneNumber: str("phoneNumber", "PhoneNumber"),
    city: str("city", "City"),
    country: str("country", "Country"),
    gender: str("gender", "Gender"),
    languagePreference: str("languagePreference", "LanguagePreference"),
    professionalPhoto,
    ProfessionalPhoto: professionalPhoto,
    aboutYou: str("aboutYou", "AboutYou"),
    educationLevel: str("educationLevel", "EducationLevel"),
    studyInterest: str("studyInterest", "StudyInterest"),
    skillsExperience: str("skillsExperience", "SkillsExperience"),
    mentorshipObjectives: str("mentorshipObjectives", "MentorshipObjectives"),
    mentorshipExpertise: str("mentorshipExpertise", "MentorshipExpertise"),
    mentorshipFormat: str("mentorshipFormat", "MentorshipFormat"),
    platformInterest: str("platformInterest", "PlatformInterest"),
    peerNetworking: str("peerNetworking", "PeerNetworking"),
    productInterested: str("productInterested", "ProductInterested"),
    businessName: str("businessName", "BusinessName"),
    businessLogo: str("businessLogo", "BusinessLogo"),
    businessSectorIndustry: str(
      "businessSectorIndustry",
      "BusinessSectorIndustry"
    ),
    businessWebsiteUrl: str("businessWebsiteUrl", "BusinessWebsiteUrl"),
    businessCountryOperation: str(
      "businessCountryOperation",
      "BusinessCountryOperation"
    ),
    businessAbout: str("businessAbout", "BusinessAbout"),
    businessAddressLineOne: str(
      "businessAddressLineOne",
      "BusinessAddressLineOne"
    ),
    businessAddressLineTwo: str(
      "businessAddressLineTwo",
      "BusinessAddressLineTwo"
    ),
    businessCountry: str("businessCountry", "BusinessCountry"),
    businessPostCode: str("businessPostCode", "BusinessPostCode"),
    businessTelephoneNumber: str(
      "businessTelephoneNumber",
      "BusinessTelephoneNumber"
    ),
    businessCurrency: str("businessCurrency", "BusinessCurrency"),
    businessTurnover: str("businessTurnover", "BusinessTurnover"),
    businessObjectives: str("businessObjectives", "BusinessObjectives"),
    businessProductServices: str(
      "businessProductServices",
      "BusinessProductServices"
    ),
    businessDiscount: str("businessDiscount", "BusinessDiscount"),
    businessDiscountCode: str("businessDiscountCode", "BusinessDiscountCode"),
    personalGender: str("personalGender", "PersonalGender"),
    personalLanguagePreference: str(
      "personalLanguagePreference",
      "PersonalLanguagePreference"
    ),
    personalAbout: str("personalAbout", "PersonalAbout"),
    personalProfessionalPhoto,
    PersonalProfessionalPhoto: personalProfessionalPhoto,
    personalPositionCompany: str(
      "personalPositionCompany",
      "PersonalPositionCompany"
    ),
    hasCompletedProfile: 0,
    county: str("county", "County"),
    referralCode: str("referralCode", "ReferralCode"),
    referralCodeUsed: str("referralCodeUsed", "ReferralCodeUsed"),
    eventObjectives: str("eventObjectives", "EventObjectives"),
  };
}
